import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, type useRxData } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";

export const RXDB_IMAGING_INSTANCE_COLLECTION_NAME = "imaging-instance";

export const useRxCollectionImagingInstance =
  (): RxCollection<ImagingInstance> | null =>
    useRxCollection<ImagingInstance>(RXDB_IMAGING_INSTANCE_COLLECTION_NAME);

type UseRxData = Parameters<typeof useRxData<ImagingInstance>>;
export type ImagingInstanceQueryConstructor = UseRxData["1"];

const IMAGING_INSTANCE_SCHEMA_LITERAL = {
  title: "imaging instance schema",
  version: 10,
  description: "Represents an imaging instance with S3 keys",
  type: "object",
  primaryKey: "id",
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    imagingStudyAccessionNumber: {
      type: "string",
      maxLength: 100,
    },
    // PNE-221: study scope for instances without an accession number (GE VScan Air).
    // Nullable because old records pre-date this field. RxDB cannot index nullable
    // fields, so this is not indexed — collection scans scale with the total doc
    // count across all studies (not per-study), but this is acceptable in practice
    // given RxDB's data-expiry window keeps the total bounded and small.
    dicomStudyId: {
      type: ["string", "null"],
      maxLength: 100,
    },
    elephantPatientId: {
      type: ["string", "null"],
      maxLength: 100,
    },
    externalId: {
      type: "string",
    },
    previewS3Key: {
      type: "string",
    },
    thumbnailS3Key: {
      type: "string",
    },
    // PNE-202: raw GE ImageType[2] value (e.g. "TTE", "CHEST") — null for non-GE or unknown
    dicomPreset: {
      type: ["string", "null"],
    },
    // PNE-210: mm per pixel from DICOM tag (0018,602C) Physical Delta X — null if tag absent
    physicalDeltaMm: {
      type: ["number", "null"],
    },
    numberOfFrames: {
      type: ["integer", "null"],
    },
    frameRate: {
      type: ["number", "null"],
    },
    cineStatus: {
      type: ["string", "null"],
      enum: ["PENDING", "READY", "FAILED", null],
    },
    cineS3Key: {
      type: ["string", "null"],
    },
    cineDurationMs: {
      type: ["integer", "null"],
    },
    cineSizeBytes: {
      type: ["integer", "null"],
    },
    // PNE-427: DICOM device identity — null when the PACS reports unknown/empty
    deviceManufacturer: {
      type: ["string", "null"],
    },
    deviceModelName: {
      type: ["string", "null"],
    },
    meta: SYNCABLE_META,
  },
  required: [
    "id",
    "imagingStudyAccessionNumber",
    "externalId",
    "previewS3Key",
    "thumbnailS3Key",
    "meta",
  ],
  indexes: ["imagingStudyAccessionNumber"],
  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(IMAGING_INSTANCE_SCHEMA_LITERAL);

export type ImagingInstance = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export const IMAGING_INSTANCE_SCHEMA: RxJsonSchema<ImagingInstance> =
  IMAGING_INSTANCE_SCHEMA_LITERAL;
